import { useMemo } from 'react';
import {
    IconUsers,
    IconDatabase,
    IconFolder,
    IconSchool as ClassIcon,
    IconMap,
    IconList,
    IconSettings,
    IconLink,
    IconFilter,
    IconBug,
    IconCode,
    IconPencil,
    IconSearch,
    IconGauge,
    IconLock,
    IconScale,
    IconFlask,
    IconCategory,
    IconColumns3,
} from '@tabler/icons-react';
import type { QuadrantItemData } from '../components/dashboard/QuadrantItem';
import { useDashboardCounts } from './useDashboardCounts';
import { useNavigationCategorization } from './useNavigationCategorization';
import { useEntityEntitlements } from './useEntityEntitlements';
import { useIsImperator } from './useIsImperator';
import { getEntityIcon, getEntityColor } from '../utils/navigationIconUtils';
import { getPluralizedLabel, getSingularLabel } from '../utils/pluralizationUtils';
import { getReadableEntityIds, getDisjointedEntityIds, isAdministrator, canReadPerson } from '../types/EntityEntitlements';
import ImpersonationService from '../services/ImpersonationService';

const BLACKLIST_ENTITY_TYPES = new Set([
    'class', 'set', 'policyrule', 'policyruleaudit', 'pluginfunction',
    'schemabinding', 'schemaentity', 'schemaattribute',
]);

const isPeopleQuadrantOverride = (name: string) =>
    name.toLowerCase().startsWith('studenttransport');

const UTILITY_ITEMS = [
    { id: 'customFilter', label: 'Custom Filter', icon: IconFilter },
    { id: 'testing', label: 'API Testing', icon: IconBug },
    { id: 'jsTesting', label: 'JS Testing', icon: IconCode },
    { id: 'patchTesting', label: 'Patch Testing', icon: IconPencil },
    { id: 'scimSelect', label: 'SCIM Select', icon: IconSearch },
    { id: 'speedComparison', label: 'Speed Compare', icon: IconGauge },
    { id: 'searchCompare', label: 'Search Compare', icon: IconColumns3 },
    { id: 'encdec', label: 'EncDec', icon: IconLock },
    { id: 'policyRuleAudit', label: 'Policy Audit', icon: IconScale },
    { id: 'setGen', label: 'Set Generator', icon: IconFlask },
    { id: 'contentTypes', label: 'Content Types', icon: IconCategory },
    { id: 'searchIndexes', label: 'Search Indexes', icon: IconDatabase },
    { id: 'userSettings', label: 'User Settings', icon: IconSettings },
    { id: 'freeEdit', label: 'Free Edit', icon: IconCode },
];

const ADMIN_STATIC_ITEMS: { name: string; pluralizerKey: string; entitlementId: string }[] = [
    { name: 'workflow', pluralizerKey: 'Workflow', entitlementId: 'Workflow' },
    { name: 'WorkflowActivity', pluralizerKey: 'WorkflowActivity', entitlementId: 'WorkflowActivity' },
    { name: 'functionCatalog', pluralizerKey: 'FunctionCatalog', entitlementId: 'FunctionCatalog' },
    { name: 'pluginfunctions', pluralizerKey: 'PluginFunction', entitlementId: 'PluginFunction' },
];

interface UseQuadrantItemsOptions {
    onNavigate: (
        destination: string,
        isEntity?: boolean,
        category?: 'person' | 'locality' | 'other' | 'meta' | 'utilities' | 'blacklist' | 'admin'
    ) => void;
    authResolved: boolean;
}

export const useQuadrantItems = ({ onNavigate, authResolved: _authResolved }: UseQuadrantItemsOptions) => {
    const { entityEntitlements, loading: entitlementsLoading } = useEntityEntitlements();
    const isImperator = useIsImperator();
    const onBehalfOf = ImpersonationService.getIsOnBehalfOfEffective();

    const entitlementEntityIds = useMemo(
        () => entityEntitlements?.EntityEntitlements?.map((e: any) => e.Id).filter(Boolean) ?? [],
        [entityEntitlements]
    );
    const readableEntityIds = useMemo(() => getReadableEntityIds(entityEntitlements), [entityEntitlements]);
    /** Case-insensitive set for filtering admin/other/meta by tier1 (Id casing may vary). */
    const readableIdsLower = useMemo(
        () => (readableEntityIds ? new Set([...readableEntityIds].map(id => (id ?? '').toString().trim().toLowerCase())) : new Set<string>()),
        [readableEntityIds]
    );
    const disjointedEntityIds = useMemo(() => getDisjointedEntityIds(entityEntitlements), [entityEntitlements]);
    const isAdmin = isAdministrator(entityEntitlements);
    const isDisjointedType = (entityType: string) => disjointedEntityIds.has((entityType || '').trim().toLowerCase());
    const hasEntitlements = entityEntitlements != null;

    // Prevent leaking entity types/tiles before we know what the user can access.
    // For authenticated flows, tier1 entitlements is the gate for nav visibility.
    const gateOnEntitlements = _authResolved && entitlementsLoading && !hasEntitlements;

    const {
        entityCategories,
        categorizationLoading,
        entityTypesLoading,
        entityTypes,
        hasGroups,
        hasClasses,
        hasLocalities,
        hasOther,
        hasAdmin,
        hasMeta,
    } = useNavigationCategorization(entitlementEntityIds);

    const { counts, loading: countsLoading } = useDashboardCounts();

    const personTypes = useMemo(() => {
        if (entityTypesLoading || categorizationLoading) return [];
        return entityTypes
            .filter((t: any) => {
                const lower = t.name.toLowerCase();
                return entityCategories.get(t.name) === 'person' &&
                    !['users', 'user', 'person', 'persons'].includes(lower);
            })
            .filter((t: any) => !hasEntitlements || isAdmin || readableEntityIds.has(t.name));
    }, [entityTypes, entityCategories, entityTypesLoading, categorizationLoading, hasEntitlements, isAdmin, readableEntityIds]);

    const localityTypes = useMemo(() => {
        if (entityTypesLoading || categorizationLoading) return [];
        return entityTypes
            .filter((t: any) => entityCategories.get(t.name) === 'locality')
            .filter((t: any) => !hasEntitlements || isAdmin || readableEntityIds.has(t.name));
    }, [entityTypes, entityCategories, entityTypesLoading, categorizationLoading, hasEntitlements, isAdmin, readableEntityIds]);

    const otherTypes = useMemo(() => {
        if (entityTypesLoading || categorizationLoading) return [];
        return entityTypes
            .filter((t: any) => {
                const lower = t.name.toLowerCase();
                return entityCategories.get(t.name) === 'other' && !BLACKLIST_ENTITY_TYPES.has(lower);
            })
            .filter((t: any) => !hasEntitlements || isAdmin || readableEntityIds.has(t.name));
    }, [entityTypes, entityCategories, entityTypesLoading, categorizationLoading, hasEntitlements, isAdmin, readableEntityIds]);

    const adminTypes = useMemo(() => {
        if (entityTypesLoading || categorizationLoading) return [];
        const staticNames = new Set(ADMIN_STATIC_ITEMS.map(i => i.name.toLowerCase()));
        return entityTypes
            .filter((t: any) => {
                const lower = t.name.toLowerCase();
                return entityCategories.get(t.name) === 'admin' &&
                    !staticNames.has(lower) &&
                    lower !== 'pluginfunction';
            })
            .filter((t: any) => !hasEntitlements || isAdmin || readableIdsLower.has((t.name ?? '').toString().trim().toLowerCase()));
    }, [entityTypes, entityCategories, entityTypesLoading, categorizationLoading, hasEntitlements, isAdmin, readableIdsLower]);

    const metaTypes = useMemo(() => {
        if (entityTypesLoading || categorizationLoading) return [];
        const staticNames = new Set(['resourcetypes', 'serviceproviderconfig', 'schemabinding', 'schemaentity', 'schemaattribute']);
        return entityTypes
            .filter((t: any) => {
                const lower = t.name.toLowerCase();
                return entityCategories.get(t.name) === 'meta' && !staticNames.has(lower);
            })
            .filter((t: any) => !hasEntitlements || isAdmin || readableEntityIds.has(t.name));
    }, [entityTypes, entityCategories, entityTypesLoading, categorizationLoading, hasEntitlements, isAdmin, readableEntityIds]);

    const showPersonSection = canReadPerson(entityEntitlements, readableEntityIds) || !hasEntitlements;
    const showSchemaBindings = hasEntitlements ? (isAdmin || readableEntityIds.has('SchemaBinding')) : true;
    const showSchemaEntities = hasEntitlements ? (isAdmin || readableEntityIds.has('SchemaEntity')) : true;
    const showSchemaAttributes = hasEntitlements ? (isAdmin || readableEntityIds.has('SchemaAttribute')) : true;
    const showResourceTypes = (isImperator && !onBehalfOf) || isAdmin;
    const showServiceProviderConfig = (isImperator && !onBehalfOf) || isAdmin;

    const quadrantItems = useMemo<QuadrantItemData[]>(() => {
        const items: QuadrantItemData[] = [];

        // --- Quadrant 0 (TL): People ---
        if (showPersonSection) {
            items.push({
                id: 'allUsers',
                label: 'All Users' + (isDisjointedType('Person') ? '\u00A0*' : ''),
                icon: IconUsers,
                color: '#1976d2',
                count: counts.get('Person'),
                countLoading: countsLoading,
                onClick: () => onNavigate('Person', false),
                quadrant: 0,
            });
            personTypes.forEach((t: any) => {
                items.push({
                    id: `person-${t.name}`,
                    label: getPluralizedLabel(t.name) + (isDisjointedType(t.name) ? '\u00A0*' : ''),
                    sortLabel: getSingularLabel(t.name),
                    icon: getEntityIcon(t.name),
                    color: getEntityColor(t.name),
                    count: counts.get(t.name),
                    countLoading: countsLoading,
                    onClick: () => onNavigate(t.name, true, 'person'),
                    quadrant: 0,
                });
            });
            [...localityTypes, ...otherTypes]
                .filter((t: any) => isPeopleQuadrantOverride(t.name))
                .forEach((t: any) => {
                    const originalCategory = entityCategories.get(t.name) as 'locality' | 'other';
                    items.push({
                        id: `person-${t.name}`,
                        label: getPluralizedLabel(t.name) + (isDisjointedType(t.name) ? '\u00A0*' : ''),
                        sortLabel: getSingularLabel(t.name),
                        icon: getEntityIcon(t.name),
                        color: getEntityColor(t.name),
                        count: counts.get(t.name),
                    countLoading: countsLoading,
                        onClick: () => onNavigate(t.name, true, originalCategory),
                        quadrant: 0,
                    });
                });
        }

        // --- Quadrant 1 (TR): Core & Places ---
        if (hasGroups && (!hasEntitlements || isAdmin || readableEntityIds.has('Group'))) {
            items.push({
                id: 'groups',
                label: 'Groups' + (isDisjointedType('Group') ? '\u00A0*' : ''),
                icon: IconFolder,
                color: '#7b1fa2',
                count: counts.get('Group'),
                countLoading: countsLoading,
                onClick: () => onNavigate('groups', true, 'other'),
                quadrant: 1,
            });
        }
        if (hasClasses && (!hasEntitlements || isAdmin || readableEntityIds.has('Class'))) {
            items.push({
                id: 'classes',
                label: 'Classes' + (isDisjointedType('Class') ? '\u00A0*' : ''),
                icon: ClassIcon,
                color: '#f57c00',
                count: counts.get('Class'),
                countLoading: countsLoading,
                onClick: () => onNavigate('classes', true, 'other'),
                quadrant: 1,
            });
        }
        if (!hasEntitlements || isAdmin || readableEntityIds.has('School')) {
            items.push({
                id: 'schoolMap',
                label: 'School Map',
                icon: IconMap,
                color: '#388e3c',
                onClick: () => onNavigate('schoolMap', false),
                quadrant: 1,
            });
        }
        if (hasLocalities) {
            localityTypes
                .filter((t: any) => !isPeopleQuadrantOverride(t.name))
                .forEach((t: any) => {
                    items.push({
                        id: `locality-${t.name}`,
                        label: getPluralizedLabel(t.name) + (isDisjointedType(t.name) ? '\u00A0*' : ''),
                        sortLabel: getSingularLabel(t.name),
                        icon: getEntityIcon(t.name),
                        color: getEntityColor(t.name),
                        count: counts.get(t.name),
                        countLoading: countsLoading,
                        onClick: () => onNavigate(t.name, true, 'locality'),
                        quadrant: 1,
                    });
                });
        }

        // --- Quadrant 2 (BL): Administration ---
        if (hasAdmin) {
            const allowedStaticAdmin = hasEntitlements && !isAdmin
                ? ADMIN_STATIC_ITEMS.filter(item =>
                    readableIdsLower.has((item.entitlementId ?? '').toString().trim().toLowerCase()) ||
                    readableIdsLower.has((item.name ?? '').toString().trim().toLowerCase())
                )
                : ADMIN_STATIC_ITEMS;
            allowedStaticAdmin.forEach(item => {
                // Icon/color should be driven by the entity/entitlement key (singular),
                // not the route slug (which can be plural like "pluginfunctions").
                const Icon = getEntityIcon(item.entitlementId);
                const color = getEntityColor(item.entitlementId);
                const isEntityType = item.name !== 'functionCatalog' && item.name !== 'pluginfunctions';
                items.push({
                    id: `admin-${item.name}`,
                    label: getPluralizedLabel(item.pluralizerKey) + (isDisjointedType(item.name) ? '\u00A0*' : ''),
                    sortLabel: getSingularLabel(item.pluralizerKey),
                    icon: Icon,
                    color,
                    count: isEntityType ? counts.get(item.name) : undefined,
                    countLoading: isEntityType ? countsLoading : false,
                    onClick: () => {
                        if (item.name === 'functionCatalog') {
                            onNavigate('admin/functionCatalog', false, 'admin');
                        } else if (item.name === 'pluginfunctions') {
                            onNavigate('pluginfunctions', true, 'admin');
                        } else {
                            onNavigate(item.name, true, 'admin');
                        }
                    },
                    quadrant: 2,
                });
            });
            adminTypes.forEach((t: any) => {
                items.push({
                    id: `adminEntity-${t.name}`,
                    label: getPluralizedLabel(t.name) + (isDisjointedType(t.name) ? '\u00A0*' : ''),
                    sortLabel: getSingularLabel(t.name),
                    icon: getEntityIcon(t.name),
                    color: getEntityColor(t.name),
                    count: counts.get(t.name),
                    countLoading: countsLoading,
                    onClick: () => onNavigate(t.name, true, 'admin'),
                    quadrant: 2,
                });
            });
        }
        if (hasOther) {
            otherTypes
                .filter((t: any) => !isPeopleQuadrantOverride(t.name))
                .forEach((t: any) => {
                    items.push({
                        id: `other-${t.name}`,
                        label: getPluralizedLabel(t.name) + (isDisjointedType(t.name) ? '\u00A0*' : ''),
                        sortLabel: getSingularLabel(t.name),
                        icon: getEntityIcon(t.name),
                        color: getEntityColor(t.name),
                        count: counts.get(t.name),
                        countLoading: countsLoading,
                        onClick: () => onNavigate(t.name, true, 'other'),
                        quadrant: 2,
                    });
                });
        }

        // --- Quadrant 3 (BR): Tools & Meta ---
        if (hasMeta) {
            if (showResourceTypes) {
                items.push({
                    id: 'meta-resourceTypes',
                    label: 'Resource Types',
                    icon: IconList,
                    color: '#607d8b',
                    onClick: () => onNavigate('meta/resourceTypes', false, 'meta'),
                    quadrant: 3,
                });
            }
            if (showServiceProviderConfig) {
                items.push({
                    id: 'meta-spc',
                    label: 'Service Provider',
                    icon: IconSettings,
                    color: '#455a64',
                    onClick: () => onNavigate('meta/serviceProviderConfig', false, 'meta'),
                    quadrant: 3,
                });
            }
            if (showSchemaBindings) {
                items.push({
                    id: 'meta-schemaBindings',
                    label: 'Schema Bindings' + (isDisjointedType('SchemaBinding') ? '\u00A0*' : ''),
                    icon: IconLink,
                    color: '#546e7a',
                    onClick: () => onNavigate('meta/schemabinding', true, 'meta'),
                    quadrant: 3,
                });
            }
            if (showSchemaEntities) {
                items.push({
                    id: 'meta-schemaEntities',
                    label: 'Schema Entities' + (isDisjointedType('SchemaEntity') ? '\u00A0*' : ''),
                    icon: getEntityIcon('schemaentity'),
                    color: getEntityColor('schemaentity'),
                    onClick: () => onNavigate('meta/schemaentity', true, 'meta'),
                    quadrant: 3,
                });
            }
            if (showSchemaAttributes) {
                items.push({
                    id: 'meta-schemaAttributes',
                    label: 'Schema Attributes' + (isDisjointedType('SchemaAttribute') ? '\u00A0*' : ''),
                    icon: getEntityIcon('schemaattribute'),
                    color: getEntityColor('schemaattribute'),
                    onClick: () => onNavigate('meta/schemaattribute', true, 'meta'),
                    quadrant: 3,
                });
            }
            metaTypes.forEach((t: any) => {
                items.push({
                    id: `meta-${t.name}`,
                    label: getPluralizedLabel(t.name) + (isDisjointedType(t.name) ? '\u00A0*' : ''),
                    sortLabel: getSingularLabel(t.name),
                    icon: getEntityIcon(t.name),
                    color: getEntityColor(t.name),
                    count: counts.get(t.name),
                    countLoading: countsLoading,
                    onClick: () => onNavigate(t.name, true, 'meta'),
                    quadrant: 3,
                });
            });
        }
        UTILITY_ITEMS.forEach(item => {
            items.push({
                id: `util-${item.id}`,
                label: item.label,
                icon: item.icon,
                color: '#795548',
                onClick: () => onNavigate(item.id, false, 'utilities'),
                quadrant: 3,
            });
        });

        // Sort: by quadrant, then within each category. Use sortLabel (singular) when set so e.g. "Set" sorts before "Set Transition"; display stays plural.
        const labelCompare = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });
        const sortKey = (item: QuadrantItemData) => item.sortLabel ?? item.label;
        items.sort((a, b) => {
            if (a.quadrant !== b.quadrant) return a.quadrant - b.quadrant;
            if (a.quadrant === 0) {
                if (a.id === 'allUsers') return -1;
                if (b.id === 'allUsers') return 1;
                return labelCompare(sortKey(a), sortKey(b));
            }
            if (a.quadrant === 1 || a.quadrant === 2) return labelCompare(sortKey(a), sortKey(b));
            if (a.quadrant === 3) {
                const aMeta = a.id.startsWith('meta-');
                const bMeta = b.id.startsWith('meta-');
                if (aMeta && !bMeta) return -1;
                if (!aMeta && bMeta) return 1;
                return labelCompare(sortKey(a), sortKey(b));
            }
            return 0;
        });

        return items;
    }, [
        showPersonSection, personTypes, hasGroups, hasClasses, hasLocalities, hasOther, hasAdmin, hasMeta,
        localityTypes, otherTypes, adminTypes, metaTypes,
        counts, countsLoading, hasEntitlements, isAdmin, readableEntityIds, disjointedEntityIds,
        showResourceTypes, showServiceProviderConfig, showSchemaBindings, showSchemaEntities, showSchemaAttributes,
        onNavigate, entityCategories,
    ]);

    return {
        quadrantItems: gateOnEntitlements ? [] : quadrantItems,
        loading: gateOnEntitlements || entityTypesLoading || categorizationLoading,
    };
};
